// ---------------------------------------------------------------------------
// Search event tracker — fire-and-forget to Vercel backend
// ---------------------------------------------------------------------------
(function initSearchTracker() {
  const API = 'https://coracrea-website.vercel.app/api/shopify-proxy';

  function getSessionId() {
    let id = sessionStorage.getItem('_cora_sid');
    if (!id) {
      id = Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2);
      sessionStorage.setItem('_cora_sid', id);
    }
    return id;
  }

  window.trackSearchEvent = function (query, type, results) {
    if (!query || query.length < 2) return;
    const payload = {
      query,
      type: type || 'predictive',
      results: typeof results === 'number' ? results : null,
      session_id: getSessionId(),
      page_url: window.location.pathname,
    };
    const body = JSON.stringify(payload);
    const url = `${API}?endpoint=track-search`;
    try {
      fetch(url, {
        method: 'POST',
        body,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'omit',
        keepalive: true,
      }).catch(() => {});
    } catch (_e) {
      /* silently ignore tracking errors */
    }
  };

  // Track full-page search form submissions (/search?q=...)
  document.addEventListener('submit', function (e) {
    const form = e.target;
    if (!form || form.tagName !== 'FORM') return;
    const action = form.action || '';
    if (!action.includes('/search') && !form.dataset.searchForm) return;
    const input = form.querySelector('input[name="q"], input[type="search"]');
    if (!input || !input.value.trim()) return;
    window.trackSearchEvent(input.value.trim(), 'full', null);
  });
})();

class PredictiveSearch extends HTMLFormElement {
  constructor() {
    super();
    this.cachedMap = new Map();
    this.focusElement = this.input;
    this.boundClear = this.clear.bind(this);
    this.boundOnChange = FoxTheme.utils.debounce(this.onChange.bind(this), 300);
    this.boundOnFocus = this.onFocus.bind(this);
    this.boundClickOutside = this.handleClickOutside.bind(this);

    this.resetButton.addEventListener('click', this.boundClear);
    this.input.addEventListener('input', this.boundOnChange);
    this.input.addEventListener('focus', this.boundOnFocus);

    const isDev =
      window.location.hostname.includes('myshopify.com') ||
      window.location.hostname === 'localhost';
    this.error = isDev ? console.error.bind(console) : function () {};

    const isTemplateSearch = this.closest('.template-search');
    if (isTemplateSearch) {
      document.addEventListener('click', this.boundClickOutside);
    }
  }

  disconnectedCallback() {
    if (this.resetButton) {
      this.resetButton.removeEventListener('click', this.boundClear);
    }
    if (this.input) {
      this.input.removeEventListener('input', this.boundOnChange);
      this.input.removeEventListener('focus', this.boundOnFocus);
    }
    document.removeEventListener('click', this.boundClickOutside);
    this.cachedMap.clear();
  }

  get input() {
    return this.querySelector('input[type="search"]');
  }
  get resetButton() {
    return this.querySelector('button[type="reset"]');
  }

  onFocus(event) {
    if (this.closest('.template-search')) {
      document.body.classList.add('predictive-search-open');
      if (this.getQuery().length === 0) {
        return;
      }
      const url = this.setupURL().toString();
      this.renderSection(url, event);
    }
  }
  getQuery() {
    return this.input.value.trim();
  }
  clear(event = null) {
    event && event.preventDefault();
    this.input.value = '';
    this.input.focus();
    this.removeAttribute('results');
  }

  setupURL() {
    const url = new URL(`${FoxTheme.routes.shop_url}${FoxTheme.routes.predictive_search_url}`);
    url.searchParams.set('q', this.getQuery());
    url.searchParams.set('resources[limit]', this.dataset.resultsLimit || 3);
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('resources[options][fields]', 'title,body,vendor,product_type,tag');
    url.searchParams.set('section_id', FoxTheme.utils.getSectionId(this));
    return url;
  }

  onChange() {
    const query = this.getQuery();
    if (query.length === 0) {
      this.clear();
      return;
    }

    // Track search in GA4
    if (window.trackSearch) {
      window.trackSearch(query);
    }

    const url = this.setupURL().toString();
    this.renderSection(url);
  }

  renderSection(url) {
    this.cachedMap.has(url) ? this.renderSectionFromCache(url) : this.renderSectionFromFetch(url);
  }

  renderSectionFromCache(url) {
    const responseText = this.cachedMap.get(url);
    this.renderSearchResults(responseText);
    this.setAttribute('results', '');
  }

  renderSectionFromFetch(url) {
    this.setAttribute('loading', 'true');
    this.resetButton && this.resetButton.classList.add('btn--loading');

    fetch(url)
      .then((response) => {
        if (!response.ok) throw new Error('Network response was not ok');
        return response.text();
      })
      .then((responseText) => {
        this.renderSearchResults(responseText);
        this.cachedMap.set(url, responseText);
      })
      .catch((err) => {
        this.error('Error fetching search results:', err);
        this.setAttribute('error', 'Failed to load data');
      })
      .finally(() => {
        this.removeAttribute('loading');
        this.resetButton && this.resetButton.classList.remove('btn--loading');
        this.setAttribute('results', 'true');
      });
  }
  renderSearchResults(responseText) {
    const id = 'PredictiveSearchResults-' + FoxTheme.utils.getSectionId(this);
    const targetElement = document.getElementById(id);

    if (!targetElement) {
      this.error(`Element with id '${id}' not found in the document.`);
      return;
    }

    const parsedDoc = new DOMParser().parseFromString(responseText, 'text/html');
    const contentElement = parsedDoc.getElementById(id);

    if (!contentElement) {
      this.error(`Element with id '${id}' not found in the parsed response.`);
      return;
    }

    targetElement.replaceChildren(
      ...Array.from(contentElement.children).map((child) => child.cloneNode(true))
    );

    const count = targetElement.querySelectorAll(
      'li, .predictive-search-result, [data-result]'
    ).length;
    if (window.trackSearchEvent) {
      window.trackSearchEvent(this.getQuery(), 'predictive', count);
    }
    if (count === 0 && window.SearchFallback) {
      window.SearchFallback.initDropdownFallback(this.getQuery(), id);
    } else if (count > 0 && window.SearchFallback && window.SearchFallback.pinBuriedTitleMatch) {
      window.SearchFallback.pinBuriedTitleMatch(this.getQuery(), id);
    }
  }

  handleClickOutside(event) {
    if (!this.contains(event.target)) {
      setTimeout(() => {
        document.body.classList.remove('predictive-search-open');
      });
    }
  }
}
customElements.define('predictive-search', PredictiveSearch, { extends: 'form' });

class SearchDrawer extends DrawerComponent {
  get requiresBodyAppended() {
    return false;
  }
  get input() {
    return this.querySelector('input[type="search"]');
  }
  get focusElement() {
    return this.input;
  }
  handleHideTransition() {
    if (this.showTransitionTimerId) {
      clearTimeout(this.showTransitionTimerId);
      this.showTransitionTimerId = null;
    }
    return super.handleHideTransition();
  }
}
customElements.define('search-drawer', SearchDrawer);
